import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import config from '../config';
import Sidebar from './Sidebar';

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState('');
  const [filterOption, setFilterOption] = useState('');
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const customersPerPage = 10;

  const fetchCustomers = useCallback(async () => {
    try {
      const limit = customersPerPage;
      const offset = (currentPage - 1) * limit;
      
      const response = await axios.get(`${config.apiBaseUrl}/api/customer/customers`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        },
        params: {
          limit,
          offset,
          search: searchText,
          filter: filterOption,
        },
      });
      
      setCustomers(response.data.customers);
      setTotalCustomers(response.data.totalCount);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  }, [currentPage, searchText, filterOption]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const handleSearch = (event) => {
    setSearchText(event.target.value);
  };

  const handleSearchSubmit = () => {
    setCurrentPage(1); // Reset to the first page on new search
    fetchCustomers();
    setShowFilters(false); // Close filters on mobile after search
  };

  const handleFilterOptionChange = (event) => {
    setFilterOption(event.target.value);
  };

  const paginate = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= Math.ceil(totalCustomers / customersPerPage)) {
      setCurrentPage(pageNumber);
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  // Function to render verified status with proper styling
  const renderVerifiedStatus = (verified) => {
    if (verified === true) {
      return (
        <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">
          Verified
        </span>
      );
    } else if (verified === false) {
      return (
        <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-semibold">
          Unverified
        </span>
      );
    } else {
      return (
        <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-semibold">
          Unknown
        </span>
      );
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-800 text-white w-full">
      <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />
      
      <div className="flex-1 flex flex-col lg:ml-0">
        {/* Mobile Header */}
        <div className="lg:hidden bg-gray-900 p-4 flex justify-between items-center">
          <button 
            onClick={toggleSidebar}
            className="text-white hover:text-gray-300"
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              fill="none" 
              viewBox="0 0 24 24" 
              strokeWidth={1.5} 
              stroke="currentColor" 
              className="w-6 h-6"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
            </svg>
          </button>
          <h1 className="text-lg font-bold">Customers</h1>
          <button 
            onClick={() => setShowFilters(!showFilters)}
            className="text-white hover:text-gray-300"
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              fill="none" 
              viewBox="0 0 24 24" 
              strokeWidth={1.5} 
              stroke="currentColor" 
              className="w-6 h-6"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
            </svg>
          </button>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-4 lg:p-6">
          {/* Desktop Header */}
          <h1 className="hidden lg:block text-3xl font-bold mb-6">All Customers</h1>
          
          {/* Search/Filter Section */}
          <div className={`mb-6 ${showFilters ? 'block' : 'hidden lg:block'}`}>
            <div className="bg-gray-700 lg:bg-transparent p-4 lg:p-0 rounded-lg lg:rounded-none">
              {/* Mobile Filter Header */}
              <div className="flex justify-between items-center mb-4 lg:hidden">
                <h2 className="text-lg font-semibold">Search & Filter</h2>
                <button 
                  onClick={() => setShowFilters(false)}
                  className="text-gray-300 hover:text-white"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Filter Option */}
                <div className="flex flex-col">
                  <label className="mb-2 text-sm lg:text-lg font-medium">Search by:</label>
                  <select
                    value={filterOption}
                    onChange={handleFilterOptionChange}
                    className="p-2 rounded text-black text-sm lg:text-base"
                  >
                    <option value="">Select Option</option>
                    <option value="name">Name</option>
                    <option value="email">Email</option>
                  </select>
                </div>

                {/* Search Text */}
                <div className="flex flex-col">
                  <label className="mb-2 text-sm lg:text-lg font-medium">Search text:</label>
                  <input
                    type="text"
                    value={searchText}
                    onChange={handleSearch}
                    className="p-2 rounded text-black text-sm lg:text-base"
                    placeholder="Enter search text"
                  />
                </div>

                {/* Search Button */}
                <div className="flex flex-col justify-end">
                  <button
                    onClick={handleSearchSubmit}
                    className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded text-sm lg:text-base transition-colors"
                  >
                    Search
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Desktop Table View */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="min-w-full bg-white text-black rounded-lg overflow-hidden">
              <thead className="bg-gray-200">
                <tr>
                  <th className="py-3 px-4 border-b text-left">Customer ID</th>
                  <th className="py-3 px-4 border-b text-left">Name</th>
                  <th className="py-3 px-4 border-b text-left">Email</th>
                  <th className="py-3 px-4 border-b text-left">Mode</th>
                  <th className="py-3 px-4 border-b text-left">Verified</th>
                  <th className="py-3 px-4 border-b text-left">Date Created</th>
                  <th className="py-3 px-4 border-b text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer, index) => (
                  <tr key={customer.userid} className={`hover:bg-gray-100 ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}>
                    <td className="py-3 px-4 border-b">{customer.userid}</td>
                    <td className="py-3 px-4 border-b">{customer.name}</td>
                    <td className="py-3 px-4 border-b">{customer.email}</td>
                    <td className="py-3 px-4 border-b">{customer.mode}</td>
                    <td className="py-3 px-4 border-b">
                      {renderVerifiedStatus(customer.verified)}
                    </td>
                    <td className="py-3 px-4 border-b">
                      {new Date(customer.created).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4 border-b">
                      <a href={`/customers/${customer.userid}`}>
                        <button className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded text-sm transition-colors">
                          View
                        </button>
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="lg:hidden space-y-4">
            {customers.map((customer) => (
              <div key={customer.userid} className="bg-white text-black rounded-lg p-4 shadow">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="font-semibold text-lg">{customer.name}</p>
                    <p className="text-sm text-gray-600">ID: {customer.userid}</p>
                  </div>
                  {renderVerifiedStatus(customer.verified)}
                </div>
                
                <div className="space-y-3 mb-4 text-sm">
                  <div>
                    <p className="text-gray-600">Email</p>
                    <p className="font-medium break-all">{customer.email}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-gray-600">Mode</p>
                      <p className="font-medium">{customer.mode}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Created</p>
                      <p className="font-medium">{new Date(customer.created).toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>
                
                <a href={`/customers/${customer.userid}`} className="block">
                  <button className="w-full bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded transition-colors">
                    View Details
                  </button>
                </a>
              </div>
            ))}
          </div>

          {/* Results Info and Pagination */}
          <div className="flex flex-col sm:flex-row justify-between items-center mt-6 gap-4">
            <div className="text-sm lg:text-base">
              Showing {customers.length} of {totalCustomers} customers
            </div>
            
            {/* Pagination */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className={`font-bold py-2 px-3 lg:px-4 rounded text-sm transition-colors ${
                  currentPage === 1 
                    ? 'bg-gray-400 cursor-not-allowed text-gray-200' 
                    : 'bg-gray-500 hover:bg-gray-700 text-white'
                }`}
              >
                Previous
              </button>
              <span className="mx-2 text-sm lg:text-base">
                Page {currentPage} of {Math.ceil(totalCustomers / customersPerPage)}
              </span>
              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage * customersPerPage >= totalCustomers}
                className={`font-bold py-2 px-3 lg:px-4 rounded text-sm transition-colors ${
                  currentPage * customersPerPage >= totalCustomers 
                    ? 'bg-gray-400 cursor-not-allowed text-gray-200' 
                    : 'bg-gray-500 hover:bg-gray-700 text-white'
                }`}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Customers;